/* Lightweight client-side document export helpers.

- printText(content, title): opens a new window with the content laid out cleanly and triggers print.
- downloadDocx(content, filename, title): emits a real OOXML .docx via a tiny zero-dependency
  STORED-only ZIP writer (no compression, no pako/jszip). Word, Pages, and Google Docs all open it.
*/

function _escape(str) {
    return (str || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function _bodyHtml(content) {
    const lines = (content || "").split(/\n\n+/);
    return lines.map((p) => `<p>${_escape(p).replace(/\n/g, "<br/>")}</p>`).join("\n");
}

export function printText(content, title) {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<!doctype html>
<html><head><meta charset="utf-8"/><title>${_escape(title || "Document")}</title>
<style>
  body { font: 12pt Georgia, "Times New Roman", serif; color: #0f172a; max-width: 7.5in; margin: 0.6in auto; }
  h1 { font: 700 18pt Georgia, serif; margin: 0 0 0.25in; color: #0F1E3D; }
  p { margin: 0 0 0.18in; line-height: 1.55; }
  @media print { body { margin: 0.5in; } }
</style></head><body>
<h1>${_escape(title || "")}</h1>
${_bodyHtml(content)}
<script>window.onload = () => { window.focus(); window.print(); };</script>
</body></html>`);
    w.document.close();
}

function _xmlEscape(s) {
    return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&apos;" }[c]));
}

function _docxParagraphs(title, content) {
    const paras = [];
    if (title) {
        paras.push(
            `<w:p><w:pPr><w:pStyle w:val="Heading1"/></w:pPr><w:r><w:rPr><w:b/><w:sz w:val="32"/><w:color w:val="0F1E3D"/></w:rPr><w:t xml:space="preserve">${_xmlEscape(title)}</w:t></w:r></w:p>`
        );
    }
    (content || "").split(/\n/).forEach((line) => {
        if (line.trim() === "") {
            paras.push("<w:p/>");
        } else {
            paras.push(
                `<w:p><w:r><w:rPr><w:sz w:val="22"/></w:rPr><w:t xml:space="preserve">${_xmlEscape(line)}</w:t></w:r></w:p>`
            );
        }
    });
    return paras.join("");
}

// --- Minimal STORED-only ZIP writer (no compression, no deps) -----------------
const CRC_TABLE = (() => {
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        t[n] = c >>> 0;
    }
    return t;
})();

function _crc32(bytes) {
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}

function _u16(v) { return [v & 0xff, (v >>> 8) & 0xff]; }
function _u32(v) { return [v & 0xff, (v >>> 8) & 0xff, (v >>> 16) & 0xff, (v >>> 24) & 0xff]; }

function _dosDateTime(d) {
    const date = ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
    const time = (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
    return { date, time };
}

function _buildZip(files) {
    // files: [{ name: string, data: Uint8Array }]
    const enc = new TextEncoder();
    const now = _dosDateTime(new Date());
    const parts = [];
    const cdEntries = [];
    let offset = 0;

    for (const f of files) {
        const nameBytes = enc.encode(f.name);
        const data = f.data;
        const crc = _crc32(data);
        const size = data.length;

        // Local file header
        const lfh = [
            ..._u32(0x04034b50),
            ..._u16(20),            // version needed
            ..._u16(0),             // flags
            ..._u16(0),             // compression = STORED
            ..._u16(now.time),
            ..._u16(now.date),
            ..._u32(crc),
            ..._u32(size),          // compressed size
            ..._u32(size),          // uncompressed size
            ..._u16(nameBytes.length),
            ..._u16(0),             // extra length
        ];
        const lfhBytes = new Uint8Array(lfh);
        parts.push(lfhBytes, nameBytes, data);

        // Central directory entry (built later — record offset now)
        cdEntries.push({ name: nameBytes, crc, size, offset, time: now.time, date: now.date });

        offset += lfhBytes.length + nameBytes.length + data.length;
    }

    const cdStart = offset;
    for (const e of cdEntries) {
        const cdh = [
            ..._u32(0x02014b50),
            ..._u16(20),            // version made by
            ..._u16(20),            // version needed
            ..._u16(0),             // flags
            ..._u16(0),             // compression
            ..._u16(e.time),
            ..._u16(e.date),
            ..._u32(e.crc),
            ..._u32(e.size),
            ..._u32(e.size),
            ..._u16(e.name.length),
            ..._u16(0),             // extra length
            ..._u16(0),             // comment length
            ..._u16(0),             // disk number
            ..._u16(0),             // internal attrs
            ..._u32(0),             // external attrs
            ..._u32(e.offset),
        ];
        const cdhBytes = new Uint8Array(cdh);
        parts.push(cdhBytes, e.name);
        offset += cdhBytes.length + e.name.length;
    }
    const cdSize = offset - cdStart;

    const eocd = new Uint8Array([
        ..._u32(0x06054b50),
        ..._u16(0),                 // disk number
        ..._u16(0),                 // disk with CD start
        ..._u16(cdEntries.length),  // entries on this disk
        ..._u16(cdEntries.length),  // total entries
        ..._u32(cdSize),
        ..._u32(cdStart),
        ..._u16(0),                 // comment length
    ]);
    parts.push(eocd);

    return new Blob(parts, { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
}
// -----------------------------------------------------------------------------

export async function downloadDocx(content, filename, title) {
    return downloadDocxMulti([{ title, content }], filename);
}

export async function downloadDocxMulti(sections, filename) {
    // sections: [{ title: string, content: string }]
    try {
        const enc = new TextEncoder();
        const parts = [];
        sections.forEach((s, i) => {
            parts.push(_docxParagraphs(s.title, s.content));
            if (i < sections.length - 1) {
                // Page break between sections
                parts.push('<w:p><w:r><w:br w:type="page"/></w:r></w:p>');
            }
        });

        const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>
</Types>`;
        const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/>
</Relationships>`;
        const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
  <w:body>
    ${parts.join("")}
    <w:sectPr><w:pgSz w:w="12240" w:h="15840"/><w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440" w:gutter="0"/></w:sectPr>
  </w:body>
</w:document>`;

        const blob = _buildZip([
            { name: "[Content_Types].xml", data: enc.encode(contentTypes) },
            { name: "_rels/.rels", data: enc.encode(rels) },
            { name: "word/document.xml", data: enc.encode(documentXml) },
        ]);

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = (filename || "document").replace(/\.docx?$/i, "") + ".docx";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error("downloadDocx failed", err);
        try { window.alert("Word export failed. Please try Print or PDF instead."); } catch (e) { /* alert may not exist in some sandboxed contexts */ }
    }
}
